// @ts-nocheck
import { getLocaleStr, getItemId, formatPriceAsFloat, getSectionTitleForItem } from 'utils/utils'
import { CONSTANTS } from 'utils/constants'

let TagManager = null
let sendEvent = null
const menuItemImpressionsAlreadySentMap = {}
let menuItemImpressionsToSendMap = {}
const IMPRESSION_SENDER_DELAY_IN_MS = 3000
let sendImpressionsTimer = null

/**
 * Init this SDK
 *
 * @param tagManager - tagManager instance
 * @param sendEventMethod - sendEvent method
 */
export const init = (tagManager, sendEventMethod) => {
	if (typeof TagManager !== 'undefined') {
		TagManager = tagManager
	} else {
		console.error('ERROR - TagManager is not defined')
	}
	sendEvent = sendEventMethod
}

/**
 * Each file has it's own sendEvent which uses the SDK to send the event so we need to call this a different name.
 *
 * @param eventName
 * @param data
 * @returns {*}
 */
export const triggerSendEvent = (eventName, ...args) => {
	if (mapAndSendEvent[eventName]) {
		mapAndSendEvent[eventName](...args)
	} else {
		console.warn(`GA4 has no ecommerce event called '${eventName}'`)
	}
}

export const generatePromotionEventData = (dealData, metaData) => {
	const items = []
	let promotion_name = dealData.refName

	if (dealData?.item) {
		const { item } = dealData
		const itemTitle = getLocaleStr(item.title, metaData.locale)

		promotion_name = itemTitle

		items.push({
			item_name: itemTitle,
			item_id: getItemId(item),
			price: formatPriceAsFloat(item.price, metaData.currency, metaData.countryCode, 1, false, true),
			coupon: dealData.refName || '',
			item_brand: metaData.brand || '',
			// item_variant: ????? It ment to be “The default/first vale that appears at item popup when user clicks at menu page“, but we do not have It
			item_category: getLocaleStr(item?.section?.title, metaData.locale),
			item_list_name: 'home',
			currency: metaData.currency,
			quantity: 1,
		})
	}

	if (dealData?.coupon) {
		promotion_name = getLocaleStr(dealData?.coupon?.title, metaData.locale)
	}

	return {
		creative_slot: Number.isNaN(dealData.index) ? -1 : dealData.index,
		promotion_id: dealData.id || -1,
		promotion_name,
		ecommerce: { items },
	}
}

/**
 * Map the generic data to the format for this file's 'strategy'.
 *
 * We send it in each mapping because we user a timer in a few of the mappings to debounce requests for impressions.
 * Otherwise we would need to provide a callback which complicates the solution.
 *
 * @type {{
 * sendViewProdutDetailEvent: mapAndSendEvent.sendViewProdutDetailEvent,
 * sendProductImpressionEvent: mapAndSendEvent.sendProductImpressionEvent,
 * sendProductClickEvent: mapAndSendEvent.sendProductClickEvent,
 * addMenuItemImpression: mapAndSendEvent.addMenuItemImpression,
 * custom: mapAndSendEvent.custom,
 * sendAddToCartEvent: mapAndSendEvent.sendAddToCartEvent,
 * sendRemoveFromCartEvent: mapAndSendEvent.sendRemoveFromCartEvent,
 * sendCheckOutEvent: mapAndSendEvent.sendCheckOutEvent,
 * sendPurchaseEvent: mapAndSendEvent.sendPurchaseEvent,
 * sendSelectPromotionEvent: mapAndSendEvent.sendSelectPromotionEvent,
 * sendViewPromotionEvent: mapAndSendEvent.sendViewPromotionEvent,
 * sendViewCartEvent: mapAndSendEvent.sendViewCartEvent,
 * }}
 */
const mapAndSendEvent = {
	/**
	 * Only send an impression for a menu-item that hasn't already had an impressions sent to GTM.
	 *
	 * Group impressions in a collection and send it every IMPRESSION_SENDER_DELAY_IN_MS seconds.
	 *
	 * @param item
	 * @param listType
	 * @param rest
	 * @param sectionTitle
	 */
	addMenuItemImpression: (item, listType, rest, sectionTitle, cartFields) => {
		// clone the item so we can add props to it without effecting the main app
		const _item = JSON.parse(JSON.stringify(item))
		_item.listType = listType
		_item.sectionTitle = sectionTitle

		const _itemTitle = getLocaleStr(_item.title, rest.locale)

		if (!menuItemImpressionsAlreadySentMap[item.id]) {
			menuItemImpressionsToSendMap[_item.id] = _item

			if (sendImpressionsTimer) {
				clearTimeout(sendImpressionsTimer)
				sendImpressionsTimer = null
			}

			sendImpressionsTimer = setTimeout(() => {
				mapAndSendEvent.sendProductImpressionEvent(rest, cartFields)
			}, IMPRESSION_SENDER_DELAY_IN_MS)
		}
	},
	sendProductImpressionEvent: (rest, cartFields) => {
		clearTimeout(sendImpressionsTimer)
		sendImpressionsTimer = null

		const _items = []

		const _coupons = cartFields.coupons

		Object.keys(menuItemImpressionsToSendMap).forEach((_itemId, _index) => {
			// update the impressions map saying this item has now been sent to GTM
			menuItemImpressionsAlreadySentMap[_itemId] = true

			const _item = menuItemImpressionsToSendMap[_itemId]

			_items.push({
				index: _index,
				item_name: getLocaleStr(_item.title, rest.locale),
				item_id: getItemId(_item),
				price: formatPriceAsFloat(_item.price, rest.currency, rest.countryCode, 1, false, true),
				item_list_name: _item.listType,
				item_category: _item.sectionTitle,
				// position: _index + 1,
				quantity: 1,
				currency: rest.currency,
				coupon: _coupons,
			})
		})

		const event = {
			dataLayer: {
				event: 'view_item_list',
				ecommerce: {
					items: _items,
				},
			},
		}

		sendEvent(event)
		menuItemImpressionsToSendMap = {}
	},
	sendProductClickEvent: (item, metaData, rest, cartFields) => {
		const _coupons = cartFields.coupons

		const event = {
			dataLayer: {
				event: 'view_item',
				ecommerce: {
					items: [
						{
							index: 0,
							item_name: metaData.itemTitle,
							item_id: getItemId(item),
							price: formatPriceAsFloat(item.price, metaData.currency, metaData.countryCode, 1, false, true),
							item_category: metaData.sectionTitle,
							item_list_name: metaData.sectionTitle,
							currency: metaData.currency,
							quantity: 1,
							coupon: _coupons,
						},
					],
				},
			},
		}

		sendEvent(event)

		// since we have no place to trigger this event from our UI and our GTM guru advised us to send it anyway, we
		// are triggering it from here!
		mapAndSendEvent.sendViewProdutDetailEvent(item, metaData, rest, cartFields)
	},
	sendViewProdutDetailEvent: (item, metaData, rest, cartFields) => {
		const _coupons = cartFields.coupons

		const event = {
			dataLayer: {
				event: 'select_item',
				ecommerce: {
					items: [
						{
							index: 0,
							item_name: metaData.itemTitle,
							item_id: getItemId(item),
							price: formatPriceAsFloat(item.price, metaData.currency, metaData.countryCode, 1, false, true),
							item_category: metaData.sectionTitle,
							item_list_name: metaData.sectionTitle,
							quantity: 1,
							currency: metaData.currency,
							coupon: _coupons,
						},
					],
				},
			},
		}

		sendEvent(event)
	},
	sendAddToCartEvent: (item, metaData, cartFields) => {
		// clone so as not to effect the original item
		const _clone = JSON.parse(JSON.stringify(item))
		_clone.description = metaData.description

		const _coupons = cartFields.coupons

		const event = {
			dataLayer: {
				event: 'add_to_cart',
				ecommerce: {
					currency: metaData.currency,
					value: formatPriceAsFloat(_clone.price, metaData.currency, metaData.countryCode, item.quantity, false),
					items: [
						{
							item_name: metaData.itemTitle,
							currency: metaData.currency,
							index: 0,
							item_id: getItemId(_clone),
							price: formatPriceAsFloat(_clone.price, metaData.currency, metaData.countryCode, 1, false, true),
							item_category: metaData.sectionTitle,
							item_list_name: metaData.sectionTitle,
							quantity: _clone.quantity,
							coupon: _coupons,
						},
					],
				},
			},
		}

		sendEvent(event)
	},
	sendRemoveFromCartEvent: (item, metaData, rest, cartFields) => {
		const _coupons = cartFields.coupons
		const event = {
			dataLayer: {
				event: 'remove_from_cart',
				ecommerce: {
					currency: metaData.currency,
					value: formatPriceAsFloat(item.total, metaData.currency, metaData.countryCode, 1, false, true),
					items: [
						{
							currency: metaData.currency,
							item_name: item.itemTitle,
							item_id: getItemId(item),
							index: 0,
							price: formatPriceAsFloat(item.price, metaData.currency, metaData.countryCode, 1, false, true),
							item_category: item.sectionTitle,
							item_list_name: item.sectionTitle,
							quantity: item.quantity,
							coupon: _coupons,
						},
					],
				},
			},
		}

		sendEvent(event)
	},
	sendViewCartEvent: (rest, cartFields) => {
		const _coupons = cartFields.coupons

		const _items = []
		Object.keys(cartFields.items).forEach((_key) => {
			const _itemArray = cartFields.items[_key]
			_itemArray.forEach((_itemElement, _index) => {
				const _fullItem = rest.items[_itemElement.itemId]
				const sectionTitle = getSectionTitleForItem(_itemElement.itemId, rest.sections, rest.locale)

				_items.push({
					index: _index,
					currency: rest.currency,
					item_name: getLocaleStr(_fullItem.title, rest.locale),
					item_id: getItemId(_fullItem),
					price: formatPriceAsFloat(_itemElement.total, rest.currency, rest.countryCode, 1, false, true),
					quantity: _itemElement.quantity,
					item_category: sectionTitle,
					item_list_name: sectionTitle,
					coupon: _coupons,
				})
			})
		})

		const ecommerce = {
			currency: rest.currency,
			value: formatPriceAsFloat(cartFields.grandTotal, rest.currency, rest.countryCode, 1, false, true),
			items: _items,
		}

		const event = {
			dataLayer: {
				event: 'view_cart',
				ecommerce,
			},
		}

		sendEvent(event)
	},
	sendCheckOutEvent: (rest, stepData, cartFields) => {
		const _coupons = cartFields.coupons
		const _items = []
		let _index = 0
		Object.keys(cartFields.items).forEach((_key) => {
			const _itemArray = cartFields.items[_key]
			_itemArray.forEach((_itemElement) => {
				const _fullItem = rest.items[_itemElement.itemId]

				const sectionTitle = getSectionTitleForItem(_itemElement.itemId, rest.sections, rest.locale)

				_items.push({
					index: _index,
					currency: rest.currency,
					item_name: getLocaleStr(_fullItem.title, rest.locale),
					item_id: getItemId(_fullItem),
					price: formatPriceAsFloat(_itemElement.total, rest.currency, rest.countryCode, 1, false, true),
					quantity: _itemElement.quantity,
					item_category: sectionTitle,
					item_list_name: sectionTitle,
					coupon: _coupons,
				})

				_index += 1
			})
		})

		const CC = CONSTANTS.CHECKOUT_STEPS
		let eventName = null

		const ecommerce = {
			currency: rest.currency,
			value: formatPriceAsFloat(cartFields.grandTotal, rest.currency, rest.countryCode, 1, false, true),
			coupon: _coupons,
			// shipping_tier:
			items: _items,
			storeID: stepData?.storeID || '',
			storeName: stepData?.storeName || '',
		}

		switch (stepData.step) {
			case CC.GOTO_CHECKOUT_PAGE:
				eventName = 'begin_checkout'
				ecommerce.shipping_tier = stepData.deliveryType || ''
				ecommerce.payment_type = stepData.paymentType || ''
				break
			case CC.SELECTED_PAYMENT_METHOD:
				eventName = 'add_payment_info'
				ecommerce.payment_type = stepData.option || ''
				break
			case CC.DELIVERY_FORM_VALID:
				eventName = 'add_shipping_info'
				ecommerce.shipping_tier = stepData.option || ''
				break
			default:
				console.error(`unknown checkout step for GA4: ${stepData.step}`)
				return
		}

		const event = {
			dataLayer: {
				event: eventName,
				ecommerce,
			},
		}

		sendEvent(event)
	},
	sendPurchaseEvent: (itemArray, orderConfirmation, metaData) => {
		const _items = []

		itemArray.forEach((_itemElement, _index) => {
			const sectionTitle = getSectionTitleForItem(_itemElement.itemId, metaData?.sections || [], metaData?.locale) || ''

			_items.push({
				index: _index,
				currency: metaData.currency,
				item_name: getLocaleStr(_itemElement.title, metaData.locale),
				item_id: getItemId(_itemElement),
				item_category: sectionTitle,
				item_list_name: sectionTitle,
				coupon: orderConfirmation.coupon || '',
				price: formatPriceAsFloat(_itemElement.total, metaData.currency, metaData.countryCode, 1, false, true),
				quantity: _itemElement.quantity || 1, // the courseList from the orderConfirm API doesn't group the same items together but has 1 menu-item per element so the quantity will then always be 1
				affiliation: orderConfirmation?.affiliation || '',
			})
		})

		const event = {
			dataLayer: {
				event: 'purchase',
				email: metaData.user?.email || '',
				first_name: metaData.user?.first_name || '',
				last_name: metaData.user?.last_name || '',
				phone_number: metaData.user?.phone_number || '',
				transaction_id: orderConfirmation.id,
				tictuk_orderid: orderConfirmation.tictuk_orderid,
				shipping_tier: orderConfirmation.shipping_tier,
				payment_type: orderConfirmation.payment_type,
				ecommerce: {
					transaction_id: orderConfirmation.id,
					currency: metaData.currency,
					value: parseFloat(orderConfirmation.revenue) || 0.0,
					tax: parseFloat(orderConfirmation.tax),
					shipping: parseFloat(orderConfirmation.shipping) || 0,
					coupon: orderConfirmation.coupon || '',
					items: _items,
				},
			},
		}
		sendEvent(event)
	},
	/**
	 * WARNING!!!
	 * 
	 * Mobile application contains similar code for mapping the ga4YumGlobal veb-view events that arrive to firebase analytics.
	 * If you are changing It - make sure to change the mobile application code as well or at list 
	 * to check if the changes are not required there.
	 */
	custom: (data) => {
		const { category, action, label, event, eventName, 'gtm.uniqueEventId': a, ...customKeys } = data

		const _customEvent = {
			event: eventName || category.toLowerCase().replaceAll(/\s/g, '_'),
			action,
			label,
		}

		// sometimes we need to add key/values that don't fit under category, action or label. So we extract and add these custome fields here
		Object.keys(customKeys).forEach((_customKey) => {
			_customEvent[_customKey] = data[_customKey]
		})

		const eventWrapper = {
			dataLayer: _customEvent,
		}

		sendEvent(eventWrapper)
	},
	pageView: (data) => {
		const _pageViewEvent = {
			event: 'page_view',
			page_title: window.document.title,
			page_location: data.label,
		}

		const event = {
			dataLayer: _pageViewEvent,
		}

		sendEvent(event)
	},
	sendSelectPromotionEvent: (dealData, metaData) => {
		sendEvent({
			dataLayer: {
				...generatePromotionEventData(dealData, metaData),
				event: 'select_promotion',
			},
		})
	},
	sendViewPromotionEvent: (dealData, metaData) => {
		sendEvent({
			dataLayer: {
				...generatePromotionEventData(dealData, metaData),
				event: 'view_promotion',
			},
		})
	},
}
